/**
 * "The Electric Ball Room" — pure resonance puzzle. Two balls hang on chains and
 * swing as a symmetric pendulum (one DOF: `theta`, + = swung inward toward each
 * other). Above the voltage threshold the balls attract (pull inward); the force
 * grows as the gap shrinks. From rest, holding full voltage only settles at a
 * small equilibrium — the player must MODULATE the wheel in rhythm with the
 * swing to pump the amplitude past the runaway point, after which attraction
 * slams them together and the chains break. Deterministic; no Babylon/DOM.
 */
export interface BallRoomState {
  t: number;
  /** Symmetric swing angle (radians); + = swung inward. */
  theta: number;
  /** Angular velocity. */
  omega: number;
  /** Smoothed voltage 0..1 (1 = max, threshold ≈ 20kV). */
  voltage: number;
  attracting: boolean;
  /** Running peak |theta| (how close to breaking). */
  peak: number;
  broken: boolean;
  cleared: boolean;
  /** Sim time when the chains broke, or -1. */
  tBreak: number;
}

export interface BallRoomInput {
  /** Wheel position 0..1 → 0..25 kV. */
  voltage: number;
}

export interface BallRoomConfig {
  omega0: number; // natural frequency
  damp: number;
  kAttract: number;
  threshold: number; // voltage fraction where attraction begins (0.8 = 20/25 kV)
  baseGap: number; // gap between balls at rest
  chainLen: number;
  minGap: number;
  ballDiameter: number; // balls explode when their centers get this close (they touch)
  voltageLerp: number; // how fast the coil follows the wheel
  releaseTime: number; // seconds from explosion to "cleared"
}

export type BallRoomEvent =
  | { type: 'attract-on' }
  | { type: 'attract-off' }
  | { type: 'explode' } // the balls touched and blew apart
  | { type: 'cleared' };

export const DEFAULT_BALLROOM_CONFIG: BallRoomConfig = {
  omega0: 1.85, // slow, weighty swing (~3.4s period) — time to pump in rhythm
  damp: 0.6, // enough that a steady hold settles (no overshoot to contact); pumping still builds
  kAttract: 0.8, // constant inward accel at full power; a steady hold settles short of contact
  threshold: 0.4, // attraction begins at 20 kV of 50 kV
  baseGap: 4,
  chainLen: 2.9, // longer chains → bigger reach, balls can meet
  minGap: 0.3,
  ballDiameter: 1.5,
  voltageLerp: 10,
  releaseTime: 1.8,
};

export function createBallRoomState(): BallRoomState {
  return {
    t: 0,
    theta: 0.001,
    omega: 0,
    voltage: 0,
    attracting: false,
    peak: 0,
    broken: false,
    cleared: false,
    tBreak: -1,
  };
}

/** kV reading for the HUD (full wheel = 50 kV). */
export function voltageKV(s: BallRoomState): number {
  return s.voltage * 50;
}

export function stepBallRoom(
  s: BallRoomState,
  input: BallRoomInput,
  dt: number,
  cfg: BallRoomConfig,
): BallRoomEvent[] {
  const events: BallRoomEvent[] = [];
  s.t += dt;
  if (s.cleared) return events;

  // The coil voltage follows the wheel with a little inertia.
  const target = Math.max(0, Math.min(1, input.voltage));
  s.voltage += (target - s.voltage) * Math.min(1, cfg.voltageLerp * dt);

  if (!s.broken) {
    const wasAttract = s.attracting;
    s.attracting = s.voltage >= cfg.threshold;
    if (s.attracting && !wasAttract) events.push({ type: 'attract-on' });
    if (!s.attracting && wasAttract) events.push({ type: 'attract-off' });

    const gap = Math.max(cfg.minGap, cfg.baseGap - 2 * cfg.chainLen * Math.sin(s.theta));
    const vAbove = s.attracting ? (s.voltage - cfg.threshold) / (1 - cfg.threshold) : 0;
    // Constant inward pull while powered (driven oscillator): a steady hold only
    // shifts the resting angle — you must pump in rhythm to grow the swing.
    const drive = cfg.kAttract * vAbove;

    const accel = -cfg.omega0 * cfg.omega0 * Math.sin(s.theta) + drive - cfg.damp * s.omega;
    s.omega += accel * dt;
    s.theta += s.omega * dt;

    const a = Math.abs(s.theta);
    if (a > s.peak) s.peak = a;
    // The balls touch → they explode and the chains blow apart.
    if (gap <= cfg.ballDiameter) {
      s.broken = true;
      s.tBreak = s.t;
      s.attracting = false;
      events.push({ type: 'explode' });
    }
  } else if (s.t - s.tBreak >= cfg.releaseTime) {
    s.cleared = true;
    events.push({ type: 'cleared' });
  }

  return events;
}
